import { Injectable, signal, computed, inject, Injector, effect } from '@angular/core';
import type {
  RemitoBaseData,
  RemitoPhoto,
  RemitoSyncPayloadOptionalCheckOut,
  Ubicacion,
  DeviceInfo,
  FingerprintId
} from '@core/remito.model';
import { NetworkService } from './network';
import { SyncQueueService } from './sync-queue';
import { IndexedDbService } from './indexed-db';
import { CheckInLocationSyncService } from './checkin-location-sync';
import { STORAGE_KEYS } from '../constants/storage-keys';
import type { PendingLocation } from './checkin-location-db';
import { buildImageFileName } from '@shared/utils/file.utils';
import { buildRemitoSyncPayload } from '@shared/utils/remito-payload';
import { LogService } from './log.service';
import { SessionLifecycleService } from './session-lifecycle';
import { BusyStateService } from '@shared/services/update/busy-state';
import { CapturePolicyService } from './capture-policy';

/**
 * Datos del remito enviado exitosamente
 */
export interface RemitoEnviado {
  id: string;
  qrCode: string;
  nombreArchivo: string;
  fechaGuardado: string;
  observacion?: string;
  cantidadFotos?: number;
}

/**
 * Modo de guardado del remito
 */
export type SaveMode = 'online' | 'offline' | 'unknown';

export type ForceSyncOutcome = 'synced' | 'blocked_requires_checkout' | 'no_pending';

/**
 * Resultado del envío de un remito
 */
export interface SendResult {
  success: boolean;
  data?: RemitoEnviado;
  error?: string;
  saveMode?: SaveMode;
  pendingSync?: boolean;
  duplicate?: boolean;
}

/**
 * Estado completo del flujo de remito
 */
export interface RemitoState {
  remito: RemitoBaseData | null;
  observation: string;
  photos: RemitoPhoto[];
  sendResult: SendResult | null;
  checkInLocation: CheckInLocationData | null;
}

export interface CheckInLocationData {
  ubicacion: Ubicacion;
  deviceInfo?: DeviceInfo;
  fingerprintId?: FingerprintId;
}

@Injectable({ providedIn: 'root' })
export class RemitoStateService {
  private static readonly LOCAL_STORAGE_KEY = 'remitoData';
  private static readonly LOCAL_STORAGE_PHOTOS_KEY = 'remitoPhotos';

  private readonly network = inject(NetworkService);
  private readonly syncQueue = inject(SyncQueueService);
  private readonly indexedDb = inject(IndexedDbService);
  private readonly checkInSync = inject(CheckInLocationSyncService);
  private readonly logger = inject(LogService);
  private readonly injector = inject(Injector);
  private readonly busyState = inject(BusyStateService);
  private readonly capturePolicy = inject(CapturePolicyService);

  private readonly _remitoData = signal<RemitoBaseData | null>(null);
  private readonly _capturedPhotos = signal<RemitoPhoto[]>([]);
  private readonly _sendResult = signal<SendResult | null>(null);
  private readonly _checkInLocation = signal<CheckInLocationData | null>(null);

  readonly remitoData = this._remitoData.asReadonly();
  readonly capturedPhotos = this._capturedPhotos.asReadonly();
  readonly capturedPhoto = computed(() => this._capturedPhotos().at(-1)?.dataUrl ?? null);
  readonly sendResult = this._sendResult.asReadonly();
  readonly checkInLocation = this._checkInLocation.asReadonly();
  readonly hasPendingRemitos = signal(false);

  constructor() {
    this.restoreCheckInLocation();
    this.restoreRemitoData();
    this.restorePhotos();

    effect(() => {
      this.busyState.setDraftRemitoActive?.(this.hasDraftRemito());
    });

    effect(() => {
      this.busyState.setDraftPhotoActive?.(this.hasDraftPhoto());
    });
  }

  readonly isSinglePhotoMode = this.capturePolicy.isSinglePhotoMode;
  readonly maxPhotosPerRemito = this.capturePolicy.maxPhotosPerRemito;
  readonly hasRemito = computed(() => this._remitoData() !== null);
  readonly hasObservation = computed(() => !!this._remitoData()?.observacion?.trim());
  readonly photoCount = computed(() => this._capturedPhotos().length);
  readonly hasPhotos = computed(() => this.photoCount() > 0);
  readonly hasPhoto = computed(() => this.hasPhotos());
  readonly hasDraftRemito = computed(() => this._remitoData() !== null && this._sendResult()?.success !== true);
  readonly hasDraftPhoto = computed(() => this.hasPhotos() && this._sendResult()?.success !== true);
  readonly hasCheckInLocation = computed(() => this._checkInLocation() !== null);
  readonly canAddMorePhotos = computed(() => this.photoCount() < this.maxPhotosPerRemito());
  readonly maxPhotosReached = computed(() => !this.canAddMorePhotos());
  readonly isComplete = computed(() => this.hasRemito() && this.hasPhotos());
  readonly isSuccess = computed(() => this._sendResult()?.success === true);
  readonly isOnline = this.network.isOnline;
  readonly isOffline = this.network.isOffline;
  readonly pendingCount = this.syncQueue.pendingCount;
  readonly failedPendingCount = this.syncQueue.failedCount;
  readonly isSyncing = this.syncQueue.isSyncing;
  readonly hasFailedSyncs = computed(() => this.failedPendingCount() > 0);
  readonly hasError = computed(() => {
    const result = this._sendResult();
    return result !== null && result.success === false;
  });
  readonly errorMessage = computed(() => this._sendResult()?.error ?? null);
  readonly state = computed<RemitoState>(() => ({
    remito: this._remitoData(),
    observation: this._remitoData()?.observacion ?? '',
    photos: this._capturedPhotos(),
    sendResult: this._sendResult(),
    checkInLocation: this._checkInLocation(),
  }));

  setRemitoData(data: RemitoBaseData): void {
    if (!data?.qrCode?.trim()) {
      throw new Error('El código QR del remito es requerido');
    }
    if (!data.fechaEscaneo) {
      throw new Error('La fecha de escaneo es requerida');
    }
    this._remitoData.set(data);
    if (typeof localStorage !== 'undefined') {
      try {
        localStorage.setItem(RemitoStateService.LOCAL_STORAGE_KEY, JSON.stringify({
          ...data,
          fechaEscaneo: data.fechaEscaneo instanceof Date ? data.fechaEscaneo.toISOString() : data.fechaEscaneo
        }));
      } catch (error) {
        this.logger.warn('🟥 [RemitoStateService.setRemitoData] Error al guardar remitoData en localStorage', {
          error: error instanceof Error ? error.message : String(error)
        });
      }
    }
  }

  setCheckInLocation(location: Ubicacion | CheckInLocationData, deviceInfo?: DeviceInfo, fingerprintId?: FingerprintId): void {
    const normalized = this.normalizeCheckInLocation(location, deviceInfo, fingerprintId);
    if (!normalized.ubicacion) {
      throw new Error('La ubicación de check-in es requerida');
    }

    this.logger.debug('[RemitoState] Guardando ubicación de check-in', {
      hasDeviceInfo: !!normalized.deviceInfo,
      hasFingerprintId: !!normalized.fingerprintId
    });

    this._checkInLocation.set(normalized);
    this.persistCheckInLocation(normalized);
  }

  clearCheckInLocation(): void {
    this._checkInLocation.set(null);
    this.persistCheckInLocation(null);
  }

  setCapturedPhoto(photo: string): void {
    this.replaceAllCapturedPhotos([this.createPhoto(photo)]);
  }

  addCapturedPhoto(photo: string): RemitoPhoto {
    const nextPhoto = this.createPhoto(photo, this.photoCount() + 1);

    if (this.isSinglePhotoMode()) {
      this.replaceAllCapturedPhotos([nextPhoto]);
      return nextPhoto;
    }

    if (!this.canAddMorePhotos()) {
      throw new Error(`Solo se permiten ${this.maxPhotosPerRemito()} fotos por remito`);
    }

    this._capturedPhotos.update(current => [...current, nextPhoto]);
    this.persistPhotos();
    return nextPhoto;
  }

  replaceCapturedPhoto(photoId: string, photo: string): void {
    const nextImageKey = this.createImageKey(this.getPageNumberByPhotoId(photoId));
    this._capturedPhotos.update(current =>
      current.map(item =>
        item.id === photoId
          ? {
              ...item,
              dataUrl: photo,
              imageKey: nextImageKey,
              createdAt: Date.now()
            }
          : item
      )
    );
    this.persistPhotos();
  }

  removeCapturedPhoto(photoId: string): void {
    this._capturedPhotos.update(current =>
      this.renumberPhotos(current.filter(item => item.id !== photoId))
    );
    this.persistPhotos();
  }

  setCapturedPhotos(photos: RemitoPhoto[]): void {
    this.replaceAllCapturedPhotos(photos);
  }

  setSendResult(result: SendResult): void {
    if (!result) {
      throw new Error('El resultado del envío es requerido');
    }
    if (result.success && !result.data) {
      throw new Error('Un envío exitoso debe incluir los datos del remito');
    }
    if (!result.success && !result.error) {
      throw new Error('Un envío fallido debe incluir un mensaje de error');
    }
    this._sendResult.set(result);
  }

  reset(options?: { clearCheckIn?: boolean; keepObservation?: boolean }): void {
    if (!options?.keepObservation) {
      this._remitoData.set(null);
    } else {
      const current = this._remitoData();
      if (current) {
        this._remitoData.set({ ...current, qrCode: current.qrCode, fechaEscaneo: current.fechaEscaneo, ubicacion: current.ubicacion, observacion: current.observacion });
      }
    }
    this._capturedPhotos.set([]);
    this._sendResult.set(null);
    if (!options?.keepObservation) {
      this.clearStoredRemitoData();
    } else {
      this.persistPhotos();
    }
    if (options?.clearCheckIn) {
      this.clearCheckInLocation();
    }
  }

  resetSendResult(): void {
    this._sendResult.set(null);
  }

  resetPhoto(): void {
    this.resetPhotos();
  }

  resetPhotos(): void {
    this._capturedPhotos.set([]);
    this.clearStoredPhotos();
  }

  clearOrphanDrafts(): void {
    this._remitoData.set(null);
    this._capturedPhotos.set([]);
    this._sendResult.set(null);
    this.clearStoredRemitoData();
  }

  getSnapshot(): Readonly<RemitoState> {
    return {
      remito: this._remitoData(),
      observation: this._remitoData()?.observacion ?? '',
      photos: this._capturedPhotos(),
      sendResult: this._sendResult(),
      checkInLocation: this._checkInLocation(),
    };
  }

  validateForSubmit(): boolean {
    if (!this._remitoData()) {
      throw new Error('No hay datos de remito para enviar');
    }

    const photoCount = this.photoCount();
    if (photoCount === 0) {
      throw new Error('No hay fotos capturadas para enviar');
    }

    if (this.isSinglePhotoMode() && photoCount !== 1) {
      throw new Error('El modo single requiere exactamente una foto');
    }

    if (photoCount > this.maxPhotosPerRemito()) {
      throw new Error(`Se excedió el máximo de ${this.maxPhotosPerRemito()} fotos por remito`);
    }

    return true;
  }

  createDeliveryPayload(checkIn: PendingLocation): RemitoSyncPayloadOptionalCheckOut {
    const remito = this._remitoData();
    if (!remito) {
      throw new Error('No hay datos de remito para construir el payload');
    }
    if (!checkIn?.ubicacion) {
      throw new Error('La información de Check-in no contiene ubicación');
    }

    const photos = this._capturedPhotos();
    if (photos.length === 0) {
      throw new Error('No hay fotos capturadas para construir el payload');
    }

    const safeRemito = remito as RemitoBaseData;
    return buildRemitoSyncPayload({
      sessionId: checkIn.sessionId,
      checkIn: {
        id: checkIn.id,
        createdAt: checkIn.createdAt,
        ubicacion: checkIn.ubicacion,
        fingerprintId: checkIn.fingerprintId,
        deviceInfo: checkIn.deviceInfo
      },
      deliveryNotes: photos.map(photo => ({
        scanData: safeRemito,
        observation: safeRemito.observacion ?? '',
        imageKey: photo.imageKey,
        image: new Blob(),
        pageNumber: photo.pageNumber
      }))
    });
  }

  createPendingCheckInFromState(): PendingLocation | null {
    const locationData = this._checkInLocation();
    if (!locationData || !locationData.ubicacion) {
      return null;
    }

    const parsedTimestamp = Date.parse(locationData.ubicacion.timestamp);
    const fingerprintId = locationData.fingerprintId ?? locationData.deviceInfo?.fingerprintId ?? 'unknown';
    const deviceInfo = locationData.deviceInfo ?? this.buildFallbackDeviceInfo(fingerprintId);

    this.logger.debug('[RemitoState] Creando check-in temporal desde estado', {
      hasSessionId: !!this.injector.get(SessionLifecycleService).getSessionId()
    });

    return {
      id: `checkin-${Date.now().toString(36)}`,
      ubicacion: locationData.ubicacion,
      sessionId: this.injector.get(SessionLifecycleService).getSessionId() || 'anonymous-' + Date.now(),
      fingerprintId,
      deviceInfo,
      createdAt: Number.isNaN(parsedTimestamp) ? Date.now() : parsedTimestamp,
      status: 'pending',
      error: 'Check-in sincronizado desde estado local'
    };
  }

  async persistToIndexedDB(): Promise<void> {
    this.busyState.setLocalSaveActive('remito-persist', true);
    this.restoreRemitoData();
    this.restorePhotos();

    this.validateForSubmit();

    const remito = this.remitoData();
    const photos = this.capturedPhotos();

    if (!remito || photos.length === 0) {
      throw new Error('Estado incompleto: no se puede persistir');
    }

    try {
      const storedCheckIn = await this.checkInSync.getStoredCheckIn();
      const checkInMetadata = storedCheckIn ?? this.createPendingCheckInFromState();

      if (checkInMetadata) {
        this.logger.debug('[RemitoState] Check-in metadata obtenida', {
          hasStoredCheckIn: !!storedCheckIn,
          hasStateCheckIn: !!this._checkInLocation()
        });
      } else {
        this.logger.warn('[RemitoState] No se pudo obtener información de Check-in');
      }

      if (!checkInMetadata) {
        throw new Error('No se encontró información de Check-in para adjuntar al remito');
      }

      const payload = this.createDeliveryPayload(checkInMetadata);
      await this.indexedDb.init();
      const alreadyExists = await this.indexedDb.existsByQrCode(remito.qrCode);

      const observacionValue = payload.deliveryNotes[0]?.observation ?? '';
      const primaryImageKey = payload.deliveryNotes[0]?.imageKey ?? this.createImageKey(1);

      if (alreadyExists) {
        this.logger.warn('Remito duplicado detectado en persistToIndexedDB', { qrCode: remito.qrCode });
        this._sendResult.set({
          success: true,
          saveMode: 'offline',
          pendingSync: true,
          duplicate: true,
          data: {
            id: 'pending',
            qrCode: remito.qrCode,
            nombreArchivo: primaryImageKey,
            fechaGuardado: new Date().toISOString(),
            observacion: observacionValue,
            cantidadFotos: photos.length
          }
        });
        this.clearStoredRemitoData();
        return;
      }

      const pendingPayload = {
        qrCode: remito.qrCode,
        imageKeys: photos.map(photo => photo.imageKey),
        images: photos.map(photo => photo.dataUrl),
        sessionId: payload.sessionId,
        checkIn: payload.checkIn,
        observation: observacionValue,
        createdAt: Date.now(),
      };

      this.logger.debug('[RemitoState] Persistiendo remito local', {
        qrCode: pendingPayload.qrCode,
        imageCount: pendingPayload.images.length,
        imageKeys: pendingPayload.imageKeys,
        hasCheckIn: !!pendingPayload.checkIn
      });

      await this.indexedDb.savePending(pendingPayload);
      await this.syncQueue.refreshStatusSnapshot();

      this._sendResult.set({
        success: true,
        saveMode: 'offline',
        pendingSync: true,
        data: {
          id: 'pending',
          qrCode: remito.qrCode,
          nombreArchivo: primaryImageKey,
          fechaGuardado: new Date().toISOString(),
          observacion: observacionValue,
          cantidadFotos: photos.length
        }
      });

      this.clearStoredRemitoData();
      this.logger.info('Remito persistido exitosamente en IndexedDB', {
        qrCode: remito.qrCode,
        imageCount: photos.length
      });
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Error al persistir en IndexedDB';
      this.logger.error('Error en persistToIndexedDB', { error: errorMsg });
      this._sendResult.set({
        success: false,
        error: errorMsg,
        saveMode: 'offline'
      });
      throw new Error(errorMsg);
    } finally {
      this.busyState.setLocalSaveActive('remito-persist', false);
    }
  }

  private resolveCheckIn(checkIn: PendingLocation | null): PendingLocation {
    if (checkIn) {
      return checkIn;
    }

    const fallback = this.createPendingCheckInFromState();
    if (!fallback) {
      throw new Error('No se encontró información de Check-in para adjuntar al remito');
    }
    return fallback;
  }

  async forceSyncAll(): Promise<ForceSyncOutcome> {
    if (!this.network.isOnline()) {
      this.logger.warn('forceSyncAll cancelado: sin conexión');
      throw new Error('No hay conexión a internet');
    }

    if (this.syncQueue.isSyncing()) {
      this.logger.warn('forceSyncAll cancelado: ya hay una sincronización en curso');
      throw new Error('Ya hay una sincronización en curso');
    }

    const pendingBefore = this.pendingCount();
    if (pendingBefore === 0) {
      this.logger.info('forceSyncAll sin pendientes');
      return 'no_pending';
    }

    this.logger.info('forceSyncAll solicitado');

    try {
      const results = await this.syncQueue.syncAll({ force: true });
      const syncedCount = results.filter(result => result.success).length;

      if (syncedCount > 0 || this.pendingCount() === 0) {
        this.logger.info('forceSyncAll completado', { syncedCount });
        return 'synced';
      }

      if (results.length === 0) {
        this.logger.info('forceSyncAll bloqueado: pendientes sin check-out elegible');
        return 'blocked_requires_checkout';
      }

      const firstError = results.find(result => !result.success)?.error;
      throw new Error(firstError ?? 'No se pudieron sincronizar los remitos');
    } catch (error) {
      this.logger.error('forceSyncAll falló', { error: error instanceof Error ? error.message : String(error) });
      throw error;
    } finally {
      await this.syncQueue.refreshStatusSnapshot();
    }
  }

  async refreshSyncIndicators(): Promise<void> {
    await this.syncQueue.refreshStatusSnapshot();
  }

  private replaceAllCapturedPhotos(photos: RemitoPhoto[]): void {
    const nextPhotos = this.isSinglePhotoMode()
      ? photos.slice(0, 1).map((photo, index) => this.normalizePhoto(photo, index + 1))
      : this.renumberPhotos(photos);
    this._capturedPhotos.set(nextPhotos);
    this.persistPhotos();
  }

  private createPhoto(dataUrl: string, pageNumber = 1): RemitoPhoto {
    if (!dataUrl?.trim()) {
      throw new Error('La foto es requerida');
    }
    if (!dataUrl.startsWith('data:image/')) {
      throw new Error('La foto debe ser una data URL válida (data:image/...)');
    }

    return {
      id: crypto.randomUUID(),
      dataUrl,
      imageKey: this.createImageKey(pageNumber),
      pageNumber,
      createdAt: Date.now()
    };
  }

  private normalizePhoto(photo: RemitoPhoto, pageNumber: number): RemitoPhoto {
    return {
      ...photo,
      pageNumber,
      imageKey: photo.imageKey || this.createImageKey(pageNumber)
    };
  }

  private renumberPhotos(photos: RemitoPhoto[]): RemitoPhoto[] {
    return photos.map((photo, index) => this.normalizePhoto(photo, index + 1));
  }

  private getPageNumberByPhotoId(photoId: string): number {
    const found = this._capturedPhotos().find(item => item.id === photoId);
    return found?.pageNumber ?? 1;
  }

  private createImageKey(pageNumber: number): string {
    const remito = this._remitoData();
    const timestamp = remito?.fechaEscaneo?.getTime() ?? Date.now();
    const qrCode = remito?.qrCode ?? 'remito';
    const baseName = buildImageFileName(qrCode, timestamp).replace(/\.jpg$/i, '');
    return `${baseName}_page-${pageNumber}.jpg`;
  }

  private persistPhotos(): void {
    if (typeof localStorage === 'undefined') {
      return;
    }

    try {
      localStorage.setItem(RemitoStateService.LOCAL_STORAGE_PHOTOS_KEY, JSON.stringify(this._capturedPhotos()));
    } catch (error) {
      this.logger.warn('🟥 [RemitoStateService.persistPhotos] Error al guardar remitoPhotos en localStorage', {
        error: error instanceof Error ? error.message : String(error)
      });
    }
  }

  private restoreRemitoData(): void {
    if (typeof localStorage === 'undefined') {
      return;
    }

    try {
      const stored = localStorage.getItem(RemitoStateService.LOCAL_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed && parsed.fechaEscaneo) {
          parsed.fechaEscaneo = new Date(parsed.fechaEscaneo);
        }
        this._remitoData.set(parsed);
      }
    } catch {
      localStorage.removeItem(RemitoStateService.LOCAL_STORAGE_KEY);
    }
  }

  private restorePhotos(): void {
    if (typeof localStorage === 'undefined') {
      return;
    }

    try {
      const storedPhotos = localStorage.getItem(RemitoStateService.LOCAL_STORAGE_PHOTOS_KEY);
      if (storedPhotos) {
        const parsed = JSON.parse(storedPhotos) as RemitoPhoto[];
        if (Array.isArray(parsed)) {
          this._capturedPhotos.set(this.renumberPhotos(parsed));
          return;
        }
      }

      const legacyPhoto = localStorage.getItem('remitoPhoto');
      if (legacyPhoto) {
        this._capturedPhotos.set([this.createPhoto(legacyPhoto, 1)]);
      }
    } catch {
      localStorage.removeItem(RemitoStateService.LOCAL_STORAGE_PHOTOS_KEY);
      localStorage.removeItem('remitoPhoto');
    }
  }

  private clearStoredPhotos(): void {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(RemitoStateService.LOCAL_STORAGE_PHOTOS_KEY);
      localStorage.removeItem('remitoPhoto');
    }
  }

  private clearStoredRemitoData(): void {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(RemitoStateService.LOCAL_STORAGE_KEY);
    }
    this.clearStoredPhotos();
  }

  private persistCheckInLocation(checkIn: CheckInLocationData | null): void {
    if (typeof localStorage === 'undefined') {
      return;
    }

    try {
      if (checkIn) {
        const resolvedFingerprintId = checkIn.fingerprintId ?? checkIn.deviceInfo?.fingerprintId ?? 'unknown';
        const resolvedDeviceInfo = checkIn.deviceInfo ?? this.buildFallbackDeviceInfo(resolvedFingerprintId);
        const dataToSave = {
          ubicacion: checkIn.ubicacion,
          deviceInfo: resolvedDeviceInfo,
          fingerprintId: resolvedFingerprintId
        };
        localStorage.setItem(STORAGE_KEYS.checkInLocation, JSON.stringify(dataToSave));
      } else {
        localStorage.removeItem(STORAGE_KEYS.checkInLocation);
      }
    } catch (error) {
      this.logger.warn('No se pudo persistir la ubicación de Check-in', { error: error instanceof Error ? error.message : String(error) });
    }
  }

  private restoreCheckInLocation(): void {
    if (typeof localStorage === 'undefined') {
      return;
    }
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.checkInLocation);
      if (!stored) {
        return;
      }
      const parsed = JSON.parse(stored) as CheckInLocationData;
      if (parsed.ubicacion && this.isValidLocation(parsed.ubicacion)) {
        this.logger.debug('[RemitoState] Restaurando ubicación de check-in', {
          hasDeviceInfo: !!parsed.deviceInfo,
          hasFingerprintId: !!parsed.fingerprintId
        });
        this._checkInLocation.set(parsed);
      } else {
        localStorage.removeItem(STORAGE_KEYS.checkInLocation);
      }
    } catch {
      localStorage.removeItem(STORAGE_KEYS.checkInLocation);
    }
  }

  private isValidLocation(location: Ubicacion | null): location is Ubicacion {
    if (!location) {
      return false;
    }
    const hasNumbers =
      typeof location.latitud === 'number' &&
      typeof location.longitud === 'number' &&
      typeof location.precision === 'number';
    const hasTimestamp = typeof location.timestamp === 'string' && !Number.isNaN(Date.parse(location.timestamp));
    return hasNumbers && hasTimestamp;
  }

  private normalizeCheckInLocation(
    location: Ubicacion | CheckInLocationData,
    deviceInfo?: DeviceInfo,
    fingerprintId?: FingerprintId
  ): CheckInLocationData {
    if ('latitud' in location && 'longitud' in location) {
      return { ubicacion: location, deviceInfo, fingerprintId };
    }
    return {
      ubicacion: location.ubicacion,
      deviceInfo: deviceInfo ?? location.deviceInfo,
      fingerprintId: fingerprintId ?? location.fingerprintId ?? location.deviceInfo?.fingerprintId
    };
  }

  private buildFallbackDeviceInfo(fingerprintId: FingerprintId): DeviceInfo {
    return {
      deviceType: 'mobile',
      os: 'Unknown',
      version: 'unknown',
      mobile: true,
      resolution: '0x0',
      networkType: 'offline',
      timeHour: 0,
      timezone: 'unknown',
      fingerprintId
    };
  }
}
